/**
 * Sarthi Mart Default Product Page Dynamic Controller
 * Real UK Distance-Based Postcode Checking, Live Cut-off Calculation, Wishlist Integration,
 * Dynamic Price & Variant handling.
 */
(function () {
  'use strict';

  // Zone 1: Exact outward postcodes for Sarthi Mart Local Store Van Delivery
  const LOCAL_OUTWARDS = [
    'HA0', 'HA1', 'HA2', 'HA3', 'HA4', 'HA5', 'HA7', 'HA8', 'HA9',
    'NW4', 'NW7', 'NW9', 'UB5', 'UB6'
  ];

  // Zone 2: Home Counties & Greater London (Short Distance - 1 business day)
  const SHORT_DISTANCE_AREAS = [
    'N', 'W', 'SW', 'SE', 'E', 'EC', 'WC',
    'UB', 'TW', 'KT', 'CR', 'SM', 'BR', 'DA', 'RM', 'IG', 'EN', 'WD',
    'AL', 'SL', 'HP', 'LU', 'SG', 'CM', 'SS', 'ME', 'RH', 'RG'
  ];

  // Zone 4: Regional / Highlands (Far Distance - 2 to 3 business days)
  const HIGHLANDS_AREAS = ['AB', 'DD'];

  // Zone 5: Offshore & Remote Islands / Northern Ireland (Very Far - 2 to 4 business days)
  const REMOTE_OFFSHORE_AREAS = ['BT', 'HS', 'ZE', 'IM', 'JE', 'GY'];

  function cleanPostcode(val) {
    return String(val || '').toUpperCase().replace(/\s+/g, '');
  }

  function getOutwardCode(postcode) {
    const clean = cleanPostcode(postcode);
    if (/^[A-Z]{1,2}\d[A-Z\d]?$/.test(clean)) return clean;
    if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(clean)) return clean.slice(0, -3);
    return '';
  }

  function getPostcodeArea(outward) {
    const match = String(outward || '').match(/^[A-Z]+/);
    return match ? match[0] : '';
  }

  function formatPostcode(postcode) {
    const clean = cleanPostcode(postcode);
    if (/^[A-Z]{1,2}\d[A-Z\d]?$/.test(clean)) return clean;
    if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(clean)) {
      return clean.slice(0, -3) + ' ' + clean.slice(-3);
    }
    return clean;
  }

  function isScottishHighlandsOutward(outward) {
    if (!outward) return false;
    if (/^(HS|IV|KW|ZE)/.test(outward)) return true;
    if (['AB37','AB38','AB54','AB55','AB56','FK17','FK18','FK19','FK20','FK21','KA27','KA28'].includes(outward)) return true;

    const pa = outward.match(/^PA(\d{1,2})$/);
    if (pa) {
      const n = parseInt(pa[1], 10);
      if ((n >= 20 && n <= 49) || (n >= 60 && n <= 78)) return true;
    }

    const ph = outward.match(/^PH(\d{1,2})$/);
    if (ph) {
      const n = parseInt(ph[1], 10);
      if ((n >= 19 && n <= 26) || (n >= 30 && n <= 40) || (n >= 42 && n <= 44) || n === 49 || n === 50) return true;
    }

    return false;
  }

  function evaluateDeliveryZone(postcode) {
    const clean = cleanPostcode(postcode);
    const outward = getOutwardCode(clean);
    const area = getPostcodeArea(outward);

    if (!outward || !area) {
      return { zone: 'INVALID', shopifyZone: 'invalid', allowed: false };
    }

    if (LOCAL_OUTWARDS.includes(outward)) {
      return {
        zone: 'ZONE_1_LOCAL',
        shopifyZone: 'zone_1_local',
        allowed: true,
        outward,
        area,
        title: 'Same-day delivery available (Local Zone)',
        type: 'local'
      };
    }

    if (area === 'BT') {
      return {
        zone: 'ZONE_NI',
        shopifyZone: 'zone_northern_ireland',
        allowed: true,
        outward,
        area,
        title: 'Northern Ireland Delivery',
        daysRange: [2, 5],
        daysLabel: '2–5 days',
        courierText: 'Parcelforce / Royal Mail',
        perkText: 'From £12.79 up to 22 kg; weight-based rates apply above 22 kg',
        type: 'remote'
      };
    }

    if (area === 'JE') {
      return {
        zone: 'ZONE_JERSEY',
        shopifyZone: 'zone_jersey',
        allowed: true,
        outward,
        area,
        title: 'Jersey Delivery',
        daysRange: [2, 5],
        daysLabel: '2–5 days',
        courierText: 'Parcelforce / Royal Mail',
        perkText: 'From £10 up to 17.5 kg; weight-based rates apply above 17.5 kg',
        type: 'remote'
      };
    }

    if (area === 'GY') {
      return {
        zone: 'ZONE_GUERNSEY',
        shopifyZone: 'zone_guernsey',
        allowed: true,
        outward,
        area,
        title: 'Guernsey Delivery',
        daysRange: [2, 8],
        daysLabel: '2–8 days',
        courierText: 'DPD',
        perkText: 'From £22 up to 27 kg; weight-based rates apply above 27 kg',
        type: 'remote'
      };
    }

    if (area === 'IM') {
      return {
        zone: 'ZONE_IOM',
        shopifyZone: 'zone_isle_of_man',
        allowed: true,
        outward,
        area,
        title: 'Isle of Man Delivery',
        daysRange: [2, 5],
        daysLabel: '2–5 days',
        courierText: 'Parcelforce / Royal Mail',
        perkText: 'From £12.79 up to 22 kg; weight-based rates apply above 22 kg',
        type: 'remote'
      };
    }

    if (isScottishHighlandsOutward(outward)) {
      return {
        zone: 'ZONE_HIGHLANDS',
        shopifyZone: 'zone_highlands',
        allowed: true,
        outward,
        area,
        title: 'Scottish Highlands & Islands Delivery',
        daysRange: [2, 4],
        daysLabel: '2–4 days',
        courierText: 'DPD',
        perkText: 'From £13.80 up to 27.5 kg; weight-based rates apply above 27.5 kg',
        type: 'remote'
      };
    }

    // Preserve the existing Isle of Wight / Isles of Scilly product-page behaviour.
    const isExistingRemoteOutward = [
      'PO30', 'PO31', 'PO32', 'PO33', 'PO34', 'PO35', 'PO36', 'PO37', 'PO38', 'PO39', 'PO40', 'PO41',
      'TR21', 'TR22', 'TR23', 'TR24', 'TR25'
    ].includes(outward);
    if (isExistingRemoteOutward) {
      return {
        zone: 'ZONE_5_REMOTE',
        shopifyZone: 'ZONE_5_REMOTE',
        allowed: true,
        outward,
        area,
        title: 'Highlands & Offshore Delivery (Remote Zone)',
        daysRange: [2, 4],
        daysLabel: '2–4 business days',
        courierText: 'Tracked regional courier',
        perkText: 'Delivery options are confirmed at checkout',
        type: 'remote'
      };
    }

    return {
      zone: SHORT_DISTANCE_AREAS.includes(area) ? 'ZONE_2_SHORT' : 'ZONE_3_MAINLAND',
      shopifyZone: 'zone_2_uk',
      allowed: true,
      outward,
      area,
      title: 'DPD UK Mainland Delivery',
      daysRange: [1, 4],
      daysLabel: 'Aim next working day; standard 2–4 working days',
      type: 'courier'
    };
  }

  function getUKDate() {
    try {
      const now = new Date();
      const ukDateStr = now.toLocaleString("en-US", { timeZone: "Europe/London" });
      return new Date(ukDateStr);
    } catch (e) {
      return new Date();
    }
  }

  function addWorkingDays(startDate, daysToAdd) {
    let d = new Date(startDate);
    let count = 0;
    while (count < daysToAdd) {
      d.setDate(d.getDate() + 1);
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0) { // Couriers deliver Mon - Sat (excluding Sunday)
        count++;
      }
    }
    return d;
  }

  function formatShortDate(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  }

  function initPostcodeChecker() {
    // Deduplicate: If there are multiple postcode cards, keep ONLY the 1st one!
    const allCards = document.querySelectorAll('.sarthi-postcode-card');
    if (allCards.length > 1) {
      for (let i = 1; i < allCards.length; i++) {
        allCards[i].remove();
      }
    }

    const card = document.querySelector('.sarthi-postcode-card');
    if (!card) return;

    const input = card.querySelector('#sarthiPostcodeInput');
    const btn = card.querySelector('#sarthiPostcodeBtn');
    const resultsContainer = card.querySelector('#sarthiDeliveryResults');
    const panelLocal = card.querySelector('.sarthi-result-local');
    const panelCourier = card.querySelector('.sarthi-result-courier');
    const panelRemote = card.querySelector('.sarthi-result-remote');
    const panelInvalid = card.querySelector('.sarthi-result-invalid');

    const cutoffHour = parseInt(card.dataset.cutoffHour || '15', 10);

    function hideAllPanels() {
      if (resultsContainer) resultsContainer.style.display = 'none';
      if (panelLocal) panelLocal.style.display = 'none';
      if (panelCourier) panelCourier.style.display = 'none';
      if (panelRemote) panelRemote.style.display = 'none';
      if (panelInvalid) panelInvalid.style.display = 'none';
    }

    function runCheck(val, saveStorage) {
      const formatted = formatPostcode(val);
      if (input) input.value = formatted;

      const evalResult = evaluateDeliveryZone(val);
      hideAllPanels();

      if (evalResult.zone === 'INVALID') {
        if (resultsContainer) resultsContainer.style.display = 'block';
        if (panelInvalid) panelInvalid.style.display = 'flex';
        return;
      }

      if (resultsContainer) resultsContainer.style.display = 'block';

      const ukNow = getUKDate();
      const currentHour = ukNow.getHours();
      const currentMinute = ukNow.getMinutes();
      const localCutoffHour = parseInt(card.dataset.cutoffHour || '15', 10);
      const courierCutoffHour = 12;

      function nextLocalDate(start) {
        const d = new Date(start);
        do {
          d.setDate(d.getDate() + 1);
        } while (d.getDay() === 2); // Tuesday is the only regular local-delivery day off.
        return d;
      }

      function nextCourierDispatchDate(start) {
        const d = new Date(start);
        do {
          d.setDate(d.getDate() + 1);
        } while (d.getDay() === 0 || d.getDay() === 6);
        return d;
      }

      if (evalResult.type === 'local') {
        if (panelLocal) {
          panelLocal.style.display = 'flex';
          const localTitle = panelLocal.querySelector('.sarthi-local-title');
          const localDays = panelLocal.querySelector('#sarthiLocalDaysText');
          const localCutoff = panelLocal.querySelector('#sarthiLocalCutoffText');

          const isLocalServiceDay = ukNow.getDay() !== 2;
          const isBeforeLocalCutoff = currentHour < localCutoffHour;

          if (isLocalServiceDay && isBeforeLocalCutoff) {
            const hrsLeft = (localCutoffHour - 1) - currentHour;
            const minsLeft = 60 - currentMinute;
            if (localTitle) localTitle.textContent = 'Same-day delivery available (Local Zone)';
            if (localDays) localDays.textContent = `Actual delivery: Today, ${formatShortDate(ukNow)}`;
            if (localCutoff) {
              localCutoff.textContent = `Order within ${hrsLeft > 0 ? hrsLeft + 'h ' : ''}${minsLeft}m for same-day delivery today`;
            }
          } else {
            const nextDay = nextLocalDate(ukNow);
            if (localTitle) localTitle.textContent = 'Next local delivery available';
            if (localDays) localDays.textContent = `Actual delivery: ${formatShortDate(nextDay)}`;
            if (localCutoff) localCutoff.textContent = 'Local delivery operates Wednesday to Monday, 4 PM–7 PM';
          }
        }
      } else if (evalResult.type === 'remote') {
        if (panelRemote) {
          panelRemote.style.display = 'flex';
          const remoteTitle = panelRemote.querySelector('.sarthi-remote-title');
          const remoteDays = panelRemote.querySelector('#sarthiRemoteDaysText');
          const remoteCourier = panelRemote.querySelector('#sarthiRemoteCourierText');
          const remotePerk = panelRemote.querySelector('#sarthiRemotePerkText');

          const canDispatchToday =
            ukNow.getDay() >= 1 && ukNow.getDay() <= 5 && currentHour < courierCutoffHour;
          const dispatchDate = canDispatchToday ? ukNow : nextCourierDispatchDate(ukNow);
          const arrivalStart = addWorkingDays(dispatchDate, evalResult.daysRange[0]);
          const arrivalEnd = addWorkingDays(dispatchDate, evalResult.daysRange[1]);

          if (remoteTitle) remoteTitle.textContent = evalResult.title;
          if (remoteDays) {
            remoteDays.textContent = `Estimated delivery: ${evalResult.daysLabel} (${formatShortDate(arrivalStart)} – ${formatShortDate(arrivalEnd)})`;
          }
          if (remoteCourier) remoteCourier.textContent = evalResult.courierText || 'Tracked courier';
          if (remotePerk) remotePerk.textContent = evalResult.perkText || 'Delivery options are confirmed at checkout';
        }
      } else {
        if (panelCourier) {
          panelCourier.style.display = 'flex';
          const courierTitle = panelCourier.querySelector('.sarthi-courier-title');
          const courierDays = panelCourier.querySelector('#sarthiCourierDaysText');
          const courierCutoff = panelCourier.querySelector('#sarthiCourierCutoffText');

          const weekday = ukNow.getDay();
          const canDispatchToday = weekday >= 1 && weekday <= 5 && currentHour < courierCutoffHour;

          if (courierTitle) courierTitle.textContent = evalResult.title;
          if (courierDays) courierDays.textContent = 'Estimated delivery: aim next working day; standard 2–4 working days';
          if (courierCutoff) {
            if (canDispatchToday) {
              const hrsLeft = (courierCutoffHour - 1) - currentHour;
              const minsLeft = 60 - currentMinute;
              courierCutoff.textContent = `Order within ${hrsLeft > 0 ? hrsLeft + 'h ' : ''}${minsLeft}m for same-working-day dispatch`;
            } else {
              courierCutoff.textContent = 'Next courier dispatch: next working day';
            }
          }
        }
      }

      if (saveStorage) {
        try {
          localStorage.setItem('delivery_postcode', formatted);
          localStorage.setItem('delivery_zone', evalResult.shopifyZone || evalResult.zone);
          localStorage.setItem('delivery_checked', 'yes');
          localStorage.setItem('delivery_checked_at', String(Date.now()));
          window.dispatchEvent(new Event('sarthi:delivery-zone-updated'));
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: {
              Delivery_Postcode: formatted,
              Delivery_Zone: evalResult.shopifyZone || evalResult.zone
            }})
          }).catch(function () {});
        } catch (e) {}
      }

      const topbarText = document.getElementById('deliveryAddressTopbarText');
      if (topbarText && formatted && evalResult.allowed) {
        topbarText.textContent = formatted;
      }
    }

    if (btn && input) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const code = (input.value || '').trim();
        if (code && !code.toLowerCase().includes('write your postcode')) {
          runCheck(code, true);
        } else {
          hideAllPanels();
          if (resultsContainer) resultsContainer.style.display = 'block';
          if (panelInvalid) panelInvalid.style.display = 'flex';
        }
      });

      input.addEventListener('input', function () {
        if (!input.value.trim()) {
          hideAllPanels();
        }
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          btn.click();
        }
      });
    }

    // Only pre-fill and run check if customer previously entered and checked their own postcode
    let storedCode = '';
    try {
      storedCode = (localStorage.getItem('delivery_postcode') || '').trim();
    } catch (e) {}

    // Never auto-fill default test postcode or placeholder text
    if (
      storedCode &&
      storedCode.toUpperCase() !== 'HA7 1HB' &&
      !storedCode.toLowerCase().includes('write your postcode')
    ) {
      runCheck(storedCode, false);
    } else {
      if (input) input.value = '';
      hideAllPanels();
    }
  }

  function initWishlistButton() {
    const wishlistBtns = document.querySelectorAll('.sarthi-wishlist-action-btn');
    if (!wishlistBtns.length) return;

    const storageKey = 'm-wishlist-products';
    let wishlistItems = [];
    try {
      wishlistItems = JSON.parse(localStorage.getItem(storageKey)) || [];
    } catch (e) {}

    wishlistBtns.forEach(function (btn) {
      const handle = btn.dataset.productHandle;
      if (handle && wishlistItems.includes(handle)) {
        btn.classList.add('added-to-wishlist');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const currentHandle = btn.dataset.productHandle;
        if (!currentHandle) return;

        let currentList = [];
        try {
          currentList = JSON.parse(localStorage.getItem(storageKey)) || [];
        } catch (err) {}

        const index = currentList.indexOf(currentHandle);
        let isNowAdded = false;

        if (index === -1) {
          currentList.push(currentHandle);
          isNowAdded = true;
          btn.classList.add('added-to-wishlist');
        } else {
          currentList.splice(index, 1);
          isNowAdded = false;
          btn.classList.remove('added-to-wishlist');
        }

        try {
          localStorage.setItem(storageKey, JSON.stringify(currentList));
        } catch (err) {}

        // Sync all wishlist buttons with the same handle
        document.querySelectorAll(`.m-wishlist-button[data-product-handle="${currentHandle}"]`).forEach(function (otherBtn) {
          if (isNowAdded) {
            otherBtn.classList.add('added-to-wishlist');
          } else {
            otherBtn.classList.remove('added-to-wishlist');
          }
        });

        // Update count badges
        const countElements = document.querySelectorAll('.m-wishlist-count');
        countElements.forEach(function (el) {
          el.textContent = currentList.length;
          if (currentList.length > 0) {
            el.classList.remove('m:hidden');
          } else {
            el.classList.add('m:hidden');
          }
        });
      });
    });
  }

  function initZoomButton() {
    const zoomPills = document.querySelectorAll('.sarthi-zoom-pill');
    zoomPills.forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = pill.closest('media-gallery') || document.querySelector('media-gallery');
        if (gallery && gallery.lightbox) {
          const activeSlide = gallery.querySelector('.swiper-slide-active') || gallery.querySelector('.m-product-media--item');
          const index = activeSlide ? Number(activeSlide.dataset.index || 0) : 0;
          gallery.lightbox.loadAndOpen(index);
        } else {
          // Fallback: click standard zoom button or active image
          const defaultZoom = document.querySelector('.m-product-media__zoom-in:not(.sarthi-zoom-pill)');
          if (defaultZoom) defaultZoom.click();
        }
      });
    });
  }

  function initVariantBoxes() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    picker.addEventListener('change', function (e) {
      const radio = e.target;
      if (radio && radio.type === 'radio') {
        const optionInner = radio.closest('.m-product-option__inner');
        if (optionInner) {
          const selectedText = optionInner.querySelector('.option-label--selected');
          if (selectedText) {
            selectedText.textContent = radio.value;
          }
        }
      }
    });
  }

  function initLivePulseAlternator() {
    const container = document.getElementById('sarthiLivePulse');
    if (!container) return;

    if (window._sarthiPulseTimer) {
      clearInterval(window._sarthiPulseTimer);
    }

    const slideBought = container.querySelector('.sarthi-pulse-bought');
    const slideViewing = container.querySelector('.sarthi-pulse-viewing');
    const viewingCountEl = container.querySelector('.sarthi-viewing-number');

    if (!slideBought || !slideViewing) return;

    const baseViewing = parseInt(container.dataset.baseViewing || '142', 10);
    let currentSlide = 0; // 0 = bought, 1 = viewing
    let currentViewing = baseViewing;

    // Enforce bought count strictly between 10 and 50
    if (boughtCountEl) {
      let bCount = parseInt(boughtCountEl.textContent || container.dataset.baseBought || '28', 10);
      if (isNaN(bCount) || bCount < 10 || bCount > 50) {
        bCount = Math.min(50, Math.max(10, isNaN(bCount) ? 28 : bCount));
        boughtCountEl.textContent = bCount;
      }
    }

    // Alternate every 2 seconds continuously
    window._sarthiPulseTimer = setInterval(function () {
      if (currentSlide === 0) {
        // Switch to Viewing
        slideBought.classList.remove('sarthi-active-slide', 'is-active');
        slideViewing.classList.add('sarthi-active-slide');
        currentSlide = 1;

        // Realistic live fluctuation (+/- 1-2 people)
        const delta = Math.floor(Math.random() * 5) - 2;
        currentViewing = Math.max(12, currentViewing + delta);
        if (viewingCountEl) {
          viewingCountEl.textContent = currentViewing;
        }
      } else {
        // Switch to Bought
        slideViewing.classList.remove('sarthi-active-slide', 'is-active');
        slideBought.classList.add('sarthi-active-slide');
        currentSlide = 0;

        // Keep bought count consistently verified between 10 and 50
        if (boughtCountEl) {
          let bCount = parseInt(boughtCountEl.textContent || '28', 10);
          if (isNaN(bCount) || bCount < 10 || bCount > 50) {
            bCount = Math.min(50, Math.max(10, isNaN(bCount) ? 28 : bCount));
            boughtCountEl.textContent = bCount;
          }
        }
      }
    }, 2000);
  }

  window.sarthiCopyCoupon = function (btn, code) {
    if (!btn) return;
    const couponCode = code || btn.getAttribute('data-coupon-code') || 'SAVE5';
    const label = btn.querySelector('.sarthi-coupon-card__btn-label');
    const copyIcon = btn.querySelector('.sarthi-coupon-card__icon--copy');
    const checkIcon = btn.querySelector('.sarthi-coupon-card__icon--check');
    const originalText = btn.getAttribute('data-copy-text') || 'Copy';
    const copiedText = btn.getAttribute('data-copied-text') || 'Copied! ✓';

    function showSuccess() {
      btn.classList.add('copied');
      if (label) label.textContent = copiedText;
      if (copyIcon) copyIcon.style.display = 'none';
      if (checkIcon) checkIcon.style.display = 'inline-block';

      clearTimeout(btn._resetTimer);
      btn._resetTimer = setTimeout(function () {
        btn.classList.remove('copied');
        if (label) label.textContent = originalText;
        if (copyIcon) copyIcon.style.display = 'inline-block';
        if (checkIcon) checkIcon.style.display = 'none';
      }, 2200);
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(couponCode).then(showSuccess).catch(function () {
        fallbackCopy(couponCode, showSuccess);
      });
    } else {
      fallbackCopy(couponCode, showSuccess);
    }

    function fallbackCopy(text, callback) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful && callback) callback();
      } catch (err) {
        console.warn('Coupon fallback copy error:', err);
      }
      document.body.removeChild(textarea);
    }
  };

  function initCouponCodeCopy() {
    document.addEventListener('click', function (e) {
      const copyBtn = e.target.closest('.sarthi-coupon-card__copy-btn');
      if (!copyBtn) return;
      e.preventDefault();

      const code = copyBtn.getAttribute('data-coupon-code') || 'SAVE5';
      window.sarthiCopyCoupon(copyBtn, code);
    });
  }

  function onDOMReady() {
    initPostcodeChecker();
    initWishlistButton();
    initZoomButton();
    initVariantBoxes();
    initLivePulseAlternator();
    initCouponCodeCopy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onDOMReady);
  } else {
    onDOMReady();
  }

  // Re-run on Shopify section load in theme editor
  document.addEventListener('shopify:section:load', onDOMReady);
})();
